// src/pages/auth/CompleteProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { User, Phone, Loader, AlertCircle, CheckCircle } from 'lucide-react';

const CompleteProfilePage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { user, userProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // טעינת נתונים קיימים
  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        phone: userProfile.phone || ''
      });
    }
  }, [userProfile]);

  // בדיקה אם הגענו מגוגל
  const fromGoogle = location.state?.fromGoogle;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // בדיקת תקינות הטופס
  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setError('נדרש שם פרטי');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('נדרש שם משפחה');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('נדרש מספר טלפון');
      return false;
    }
    
    // בדיקת פורמט טלפון
    const phoneRegex = /^05\d{8}$/;
    if (!phoneRegex.test(formData.phone.replace(/[-\s]/g, ''))) {
      setError('מספר טלפון לא תקין (צריך להתחיל ב-05 ו-10 ספרות)');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    const updateData = {
      ...formData,
      profileComplete: true,
      displayName: `${formData.firstName} ${formData.lastName}`,
      ...((!userProfile?.createdAt) && { createdAt: new Date().toISOString() })
    };

    const result = await updateUserProfile(updateData);
    
    if (result.success) {
      // הצלחה - מעבר לדף הבית
      navigate('/', { replace: true });
    } else {
      setError(result.error || 'שגיאה בעדכון הפרופיל');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        
        {/* כרטיס השלמת פרופיל */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          
          {/* כותרת */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 px-8 py-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">השלמת פרטים</h1>
            <p className="text-green-100">
              {fromGoogle 
                ? 'ברוך הבא! נא להשלים את הפרטים החסרים' 
                : 'נא להשלים את הפרטים החסרים בפרופיל'
              }
            </p>
          </div>

          {/* תוכן הטופס */}
          <div className="p-8">
            
            {/* הודעה על המשתמש הנוכחי */}
            {user?.email && (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div>
                  <span className="text-blue-700 text-sm font-medium">מחובר כ:</span>
                  <br />
                  <span className="text-blue-600 text-sm">{user.email}</span>
                </div>
              </div>
            )}
            
            {/* הודעת שגיאה */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {/* טופס */}
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* שם פרטי */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  שם פרטי *
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all duration-300"
                  placeholder="הזן שם פרטי"
                />
              </div>

              {/* שם משפחה */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  שם משפחה *
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all duration-300"
                  placeholder="הזן שם משפחה"
                />
              </div>

              {/* טלפון */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מספר טלפון *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-4 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all duration-300"
                    placeholder="05X-XXXXXXX"
                    dir="ltr"
                  />
                </div>
              </div>

              {/* הודעה על שדות חובה */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-600 text-sm">
                  <span className="text-red-500">*</span> שדות חובה
                </p>
              </div>

              {/* כפתור שמירה */}
              <button
                type="submit"
                disabled={loading}
                className={`
                  w-full py-3 px-4 rounded-xl text-lg font-bold
                  flex items-center justify-center gap-3
                  ${loading 
                    ? 'bg-gray-300 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700'
                  }
                  text-white transition-all duration-300
                  transform hover:scale-105 active:scale-95
                  shadow-lg hover:shadow-xl
                `}
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>שומר...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>השלם ופתח את האפליקציה</span>
                  </>
                )}
              </button>
            </form>

            {/* הודעה נוספת */}
            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                הפרטים ישמרו בחשבון שלך ויהיו זמינים בפעם הבאה
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;